import { useState } from 'react'
import {
  IconPlus,
  IconSearch,
  IconFileText,
  IconTrash,
  IconPencil,
  IconUsers,
  IconUser,
  IconCheck,
  IconBed,
} from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { ViatgersService, type Viatger } from '@/services/viatgers'
import { EditViatgerModal } from '@/components/viatgers/EditViatgerModal'
import { useViatgers } from '@/hooks/useViatgers'
import { useReservations } from '@/hooks/useReservations'

export default function ViatgersPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterReservation, setFilterReservation] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [showNouModal, setShowNouModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedViatger, setSelectedViatger] = useState<Viatger | null>(null)
  const { success, error } = useToast()

  // Hook for Viatgers
  const {
    data: viatgersResponse,
    isLoading: loading,
    refetch: refetchViatgers
  } = useViatgers({
    page: currentPage,
    searchTerm,
    reservaId: filterReservation,
    perPage: 20
  })

  const viatgers = viatgersResponse?.data || []
  const totalPages = viatgersResponse?.meta.total_pages || 1
  const totalItems = viatgersResponse?.meta.total || 0

  // Hook for Reservations Dropdown
  const { data: reservationsResponse } = useReservations({
    page: 1,
    searchTerm: '',
    statusFilter: 'PENDING,CONFIRMED,CHECKED_IN',
    advancedFilters: {},
    perPage: 100
  })

  const reservations = reservationsResponse?.data || []

  const handleDelete = async (id: string) => {
    if (!window.confirm('Estàs segur que vols eliminar aquest viatger?')) {
      return
    }

    try {
      await ViatgersService.delete(id)
      success('Viatger eliminat correctament')
      refetchViatgers()
    } catch (err: any) {
      error(err.message || 'Error en eliminar el viatger')
      console.error(err)
    }
  }

  const handleGenerarFormulari = async (reservaId: string) => {
    try {
      const response = await ViatgersService.generarFormulariReserva(reservaId)

      // Copiar URL al portapapeles
      navigator.clipboard.writeText(response.url)

      success('Enllaç copiat al porta-retalls!')

      // Opcional: Mostrar el enlace en un modal
      alert(`Enllaç del formulari:\n${response.url}`)
    } catch (err: any) {
      error(err.message || 'Error en generar el formulari')
      console.error(err)
    }
  }

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-'

    const date = dateString.includes('T')
      ? new Date(dateString)
      : new Date(dateString + 'T00:00:00')

    if (isNaN(date.getTime())) return 'Data invàlida'

    return date.toLocaleDateString('ca-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 relative">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-10 w-64 h-64 bg-gradient-to-br from-green-200 to-blue-200 rounded-full blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-tr from-blue-200 to-green-200 rounded-full blur-3xl opacity-15 animate-pulse delay-300"></div>
      </div>

      <div className="relative flex flex-col gap-8 py-8 px-6 md:px-8">
        {/* Header */}
        <div className="animate-in fade-in-2 slide-in-from-top-4 duration-1000">
          <div className="flex items-center justify-between bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-8 shadow-xl">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                Viatgers
              </h1>
              <p className="text-lg text-gray-600">
                <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                  Gestiona els viatgers de les reserves
                </span>
              </p>
            </div>
            <Button
              onClick={() => setShowNouModal(true)}
              className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl h-12 px-6"
            >
              <IconPlus className="mr-2 h-5 w-5" />
              Nou Viatger
            </Button>
          </div>
        </div>

        {/* Filtres i cerca */}
        <div className="animate-in fade-in-2 slide-in-from-bottom-4 duration-1000 delay-200">
          <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-8 shadow-xl">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-1 items-center space-x-4">
                <div className="relative flex-1 max-w-sm group">
                  <IconSearch className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 transition-colors duration-300 group-focus-within:text-green-600" />
                  <Input
                    placeholder="Cercar per nom, email o document..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 border-gray-200 focus:border-green-500 focus:ring-green-500 rounded-xl h-12 transition-all duration-300"
                  />
                </div>
                <Select value={filterReservation} onValueChange={setFilterReservation}>
                  <SelectTrigger className="w-64 border-gray-200 focus:border-green-500 focus:ring-green-500 rounded-xl h-12 transition-all duration-300">
                    <SelectValue placeholder="Reserva" />
                  </SelectTrigger>
                  <SelectContent className="bg-white/95 backdrop-blur-xl border-gray-200 rounded-xl shadow-xl max-h-80">
                    <SelectItem value="all" className="rounded-lg hover:bg-green-50 transition-colors duration-200">Totes les reserves</SelectItem>
                    {reservations.map(reservation => (
                      <SelectItem
                        key={reservation.id}
                        value={reservation.id}
                        className="rounded-lg hover:bg-green-50 transition-colors duration-200"
                      >
                        {reservation.guest_name} - {formatDate(reservation.data_entrada)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Estadístiques */}
        <div className="animate-in fade-in-2 slide-in-from-bottom-4 duration-1000 delay-400">
          <div className="grid gap-6 md:grid-cols-3">
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">{totalItems}</div>
                  <p className="text-gray-600 font-medium">Total Viatgers</p>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-blue-600 p-3 rounded-2xl">
                  <IconUsers className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-green-600">
                    {viatgers.filter(v => v.es_titular).length}
                  </div>
                  <p className="text-gray-600 font-medium">Titulars</p>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-3 rounded-2xl">
                  <IconUser className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-blue-600">
                    {viatgers.filter(v => v.consentiment_rgpd).length}
                  </div>
                  <p className="text-gray-600 font-medium">Amb Consentiment</p>
                </div>
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-3 rounded-2xl">
                  <IconCheck className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Taula */}
        <div className="animate-in fade-in-2 slide-in-from-bottom-4 duration-1000 delay-600">
          <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl shadow-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-green-50/50">
                  <TableHead className="font-semibold text-gray-700">Viatger</TableHead>
                  <TableHead className="font-semibold text-gray-700">Check-in</TableHead>
                  <TableHead className="font-semibold text-gray-700">Document</TableHead>
                  <TableHead className="text-right font-semibold text-gray-700">Accions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                        <span className="text-gray-600">Carregant viatgers...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : viatgers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8">
                      <div className="text-gray-500">No s'han trobat viatgers</div>
                    </TableCell>
                  </TableRow>
                ) : (
                  viatgers.map((viatger) => (
                    <TableRow key={viatger.id} className="border-gray-200 hover:bg-green-50/30 transition-all duration-200">
                      <TableCell className="font-medium">
                        <div className="flex items-center space-x-3">
                          <div className="bg-gradient-to-r from-green-500 to-blue-600 p-2 rounded-xl">
                            <IconUser className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">{viatger.nom} {viatger.cognoms}</span>
                              {viatger.es_titular && (
                                <Badge variant="default" className="bg-green-100 text-green-800">
                                  Titular
                                </Badge>
                              )}
                            </div>
                            {viatger.email && (
                              <div className="text-sm text-gray-500">{viatger.email}</div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <IconBed className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-medium">
                            {viatger.reserva_data_entrada ? formatDate(viatger.reserva_data_entrada) : '-'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <IconFileText className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium">{viatger.tipus_document}</span>
                          </div>
                          <div className="text-sm text-gray-600">{viatger.num_document}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedViatger(viatger)
                              setShowEditModal(true)
                            }}
                            className="hover:bg-purple-50 hover:border-purple-300 rounded-lg"
                          >
                            <IconPencil className="h-4 w-4 text-purple-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(viatger.id)}
                            className="hover:bg-red-50 hover:border-red-300 rounded-lg"
                          >
                            <IconTrash className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Paginació */}
        {totalPages > 1 && (
          <div className="animate-in fade-in-2 slide-in-from-bottom-4 duration-1000 delay-800">
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-6 shadow-xl">
              <div className="flex items-center justify-between">
                <p className="text-gray-600">
                  Mostrant {((currentPage - 1) * 20) + 1} a {Math.min(currentPage * 20, totalItems)} de {totalItems} viatgers
                </p>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="border-gray-200 hover:bg-green-50 hover:border-green-300 rounded-xl px-4 py-2 transition-all duration-300 disabled:opacity-50"
                  >
                    Anterior
                  </Button>
                  <span className="text-gray-700 font-medium px-3 py-2 bg-green-50 rounded-xl border border-green-200">
                    Pàgina {currentPage} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="border-gray-200 hover:bg-green-50 hover:border-green-300 rounded-xl px-4 py-2 transition-all duration-300 disabled:opacity-50"
                  >
                    Següent
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Editar Viatger */}
      {selectedViatger && (
        <EditViatgerModal
          open={showEditModal}
          onOpenChange={setShowEditModal}
          onSuccess={() => refetchViatgers()}
          viatger={selectedViatger}
        />
      )}
    </div>
  )
}
